/**
 * Translates the Postgres unique-violation on customers.email into a
 * user-readable message. The partial unique index added 2026-05-11 is
 * called `customers_email_lower_unique_idx` — the raw Postgres error is
 * scary and unactionable, so any portal write path that touches
 * customers.email funnels its error through this helper.
 */

interface PostgresLikeError {
  code?: string | null;
  message?: string | null;
  details?: string | null;
}

const UNIQUE_INDEX_NAME = 'customers_email_lower_unique_idx';

export function friendlyCustomerEmailError(err: PostgresLikeError | null | undefined): string {
  if (!err) return 'Unknown error';
  const haystack = `${err.message ?? ''} ${err.details ?? ''}`;
  if (err.code === '23505' && haystack.includes(UNIQUE_INDEX_NAME)) {
    return 'Another customer already uses this email. Search for them by email first, or use a different address.';
  }
  return err.message ?? 'Unknown error';
}

/**
 * The existing customer that owns an email, as returned by the
 * booking-manage / customer-manage duplicate-email 409 payload.
 */
export interface ExistingCustomerInfo {
  id: string;
  customer_name: string;
  email: string;
  status: string;
  is_pos: boolean;
}

/**
 * Thrown when a save fails because the customer email already belongs to
 * another customer. Carries the existing record so a caller can offer to
 * link to it instead of showing a raw database error. The booking wizard
 * catches this to show its "use existing customer?" confirmation.
 */
export class DuplicateCustomerError extends Error {
  existingCustomer: ExistingCustomerInfo;

  constructor(message: string, existingCustomer: ExistingCustomerInfo) {
    super(message);
    this.name = 'DuplicateCustomerError';
    this.existingCustomer = existingCustomer;
  }
}
